import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { PipelineEnvironment, StepAction, StepTask } from '@telespot/domain';
import {
  GenerateFindingValue,
  PipelineStepCategory,
  PipelineStepParamsBuilder,
  PipelineStepStructuredView,
  PipelineIntegrityService,
} from '@protocols/domain';
import type { PipelineStepDraftDto } from '@protocols/use-cases';

export interface PipelineStepEditorContext {
  pipelineId: string;
  environment: PipelineEnvironment;
  order: number;
  otherStepsTasks: StepTask[];
}

export interface PipelineStepEditorInput {
  context: PipelineStepEditorContext;
  step?: PipelineStepDraftDto;
  /** See `labelNamesError`; omitted means the lookup behind `step` succeeded. */
  labelNamesError?: string | null;
}

export interface ManualDraftState {
  value: Record<string, unknown> | null;
  valid: boolean;
}

/**
 * What `exitManualMode` applies: the structured view the effects layer
 * projected out of the JSON draft, plus the outcome of the label-name lookup
 * that named its uuids. Required, not optional: omitting it would silently
 * clear an outstanding failure, so every caller has to say which it means.
 */
interface ManualModeExit extends PipelineStepStructuredView {
  labelNamesError: string | null;
}

/**
 * Narrow view consumed by the `save` flow. Declared here because the state
 * owns the projection; effects depend on this view, never on the full model.
 */
export interface PipelineStepSaveSnapshot {
  context: PipelineStepEditorContext | null;
  editingStepId: string | undefined;
  task: StepTask | null;
  cloudSubject: StepAction | null;
  generateFinding: GenerateFindingValue | null;
  categories: PipelineStepCategory[];
  multiple: boolean;
  allowNewOptions: boolean;
  enableMosaicView: boolean;
  counter: boolean;
  manualDraft: ManualDraftState | null;
}

/**
 * Narrow view consumed by `enterManualMode` to seed the JSON editor.
 */
export interface ManualModeSeed {
  context: PipelineStepEditorContext | null;
  task: StepTask | null;
  categories: PipelineStepCategory[];
  multiple: boolean;
  allowNewOptions: boolean;
  enableMosaicView: boolean;
  counter: boolean;
}

/**
 * Narrow view consumed by `selectTask`; categoriesCount keeps the array internal.
 */
export interface TaskSelectionSnapshot {
  context: PipelineStepEditorContext | null;
  task: StepTask | null;
  cloudSubject: StepAction | null;
  generateFinding: GenerateFindingValue | null;
  categoriesCount: number;
}

interface PipelineStepEditorStateModel {
  context: PipelineStepEditorContext | null;
  editingStepId: string | undefined;
  task: StepTask | null;
  cloudSubject: StepAction | null;
  generateFinding: GenerateFindingValue | null;
  categories: PipelineStepCategory[];
  editingCategoryUuid: string | null;
  multiple: boolean;
  allowNewOptions: boolean;
  enableMosaicView: boolean;
  counter: boolean;
  manualDraft: ManualDraftState | null;
  baselineSignature: string | null;
  loading: boolean;
  error: string | null;
  labelNamesError: string | null;
}

const INITIAL_STATE: PipelineStepEditorStateModel = {
  context: null,
  editingStepId: undefined,
  task: null,
  cloudSubject: null,
  generateFinding: null,
  categories: [],
  editingCategoryUuid: null,
  multiple: false,
  allowNewOptions: false,
  enableMosaicView: false,
  counter: false,
  manualDraft: null,
  baselineSignature: null,
  loading: false,
  error: null,
  labelNamesError: null,
};

@Injectable()
export class PipelineStepEditorState extends ComponentStore<PipelineStepEditorStateModel> {
  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly context$ = this.select((state) => state.context);
  public readonly environment$ = this.select((state) => state.context?.environment ?? null);
  public readonly order$ = this.select((state) => state.context?.order ?? null);
  public readonly otherStepsTasks$ = this.select((state) => state.context?.otherStepsTasks ?? []);
  public readonly editingStepId$ = this.select((state) => state.editingStepId);
  public readonly task$ = this.select((state) => state.task);
  public readonly cloudSubject$ = this.select((state) => state.cloudSubject);
  public readonly generateFinding$ = this.select((state) => state.generateFinding);
  public readonly categories$ = this.select((state) => state.categories);
  public readonly editingCategoryUuid$ = this.select((state) => state.editingCategoryUuid);
  public readonly multiple$ = this.select((state) => state.multiple);
  public readonly allowNewOptions$ = this.select((state) => state.allowNewOptions);
  public readonly enableMosaicView$ = this.select((state) => state.enableMosaicView);
  public readonly counter$ = this.select((state) => state.counter);
  public readonly manualDraft$ = this.select((state) => state.manualDraft);
  public readonly manualMode$ = this.select((state) => state.manualDraft !== null);
  public readonly baselineSignature$ = this.select((state) => state.baselineSignature);
  public readonly manualParams$ = this.select((state) => state.manualDraft?.value ?? null);
  public readonly manualParamsValid$ = this.select((state) => state.manualDraft?.valid ?? true);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly error$ = this.select((state) => state.error);
  public readonly labelNamesError$ = this.select((state) => state.labelNamesError);

  /**
   * True when the current manual draft holds JSON the structured form can
   * losslessly represent (only known top-level keys). Invalid JSON or
   * custom keys mean there's no structured representation.
   */
  public readonly manualParamsStructuredCompatible$ = this.select((state) => {
    const value = state.manualDraft?.value;
    if (!value) return false;
    return PipelineStepParamsBuilder.isStructuredCompatible(value);
  });

  public readonly saveSnapshot$ = this.select(
    (state): PipelineStepSaveSnapshot => ({
      context: state.context,
      editingStepId: state.editingStepId,
      task: state.task,
      cloudSubject: state.cloudSubject,
      generateFinding: state.generateFinding,
      categories: state.categories,
      multiple: state.multiple,
      allowNewOptions: state.allowNewOptions,
      enableMosaicView: state.enableMosaicView,
      counter: state.counter,
      manualDraft: state.manualDraft,
    })
  );

  public readonly manualModeSeed$ = this.select(
    (state): ManualModeSeed => ({
      context: state.context,
      task: state.task,
      categories: state.categories,
      multiple: state.multiple,
      allowNewOptions: state.allowNewOptions,
      enableMosaicView: state.enableMosaicView,
      counter: state.counter,
    })
  );

  public readonly taskSelectionSnapshot$ = this.select(
    (state): TaskSelectionSnapshot => ({
      context: state.context,
      task: state.task,
      cloudSubject: state.cloudSubject,
      generateFinding: state.generateFinding,
      categoriesCount: state.categories.length,
    })
  );

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly initialize = this.updater((_state, input: PipelineStepEditorInput) => {
    const next: PipelineStepEditorStateModel = {
      ...INITIAL_STATE,
      context: input.context,
      labelNamesError: input.labelNamesError ?? null,
    };
    if (!input.step) return next;

    // Meta fields apply to both variants. Structured-only fields (categories,
    // flags) stay at INITIAL_STATE defaults when the input is a manual draft —
    // the caller is expected to enter Manual Mode immediately afterwards so
    // the user sees the original params, not an empty structured form.
    const withMeta: PipelineStepEditorStateModel = {
      ...next,
      editingStepId: input.step.id,
      task: input.step.task,
      cloudSubject: PipelineIntegrityService.cloudSubjectFromTrigger(input.context.environment, input.step.trigger),
      generateFinding: PipelineIntegrityService.generateFindingForEdit(
        input.context.environment,
        input.step.generateFinding
      ),
    };

    if (input.step.mode === 'manual') return withMeta;

    return {
      ...withMeta,
      categories: input.step.categories.map((category) => category.clone()),
      multiple: input.step.multiple,
      allowNewOptions: input.step.allowNewOptions,
      enableMosaicView: input.step.enableMosaicView,
      counter: input.step.counter,
    };
  });

  public readonly reset = this.updater(() => ({ ...INITIAL_STATE }));

  public readonly setTask = this.updater((state, task: StepTask | null) => ({ ...state, task }));

  public readonly resetTaskScopedFields = this.updater((state) => ({
    ...state,
    categories: [],
    editingCategoryUuid: null,
    multiple: false,
    allowNewOptions: false,
    enableMosaicView: false,
    counter: false,
    error: null,
  }));

  public readonly resetTaskScopedFlags = this.updater((state) => ({
    ...state,
    editingCategoryUuid: null,
    multiple: false,
    allowNewOptions: false,
    enableMosaicView: false,
    counter: false,
    error: null,
  }));

  public readonly setCloudSubject = this.updater((state, cloudSubject: StepAction | null) => ({
    ...state,
    cloudSubject,
  }));

  public readonly setGenerateFinding = this.updater((state, generateFinding: GenerateFindingValue | null) => ({
    ...state,
    generateFinding,
  }));

  public readonly appendCategory = this.updater((state, category: PipelineStepCategory) => ({
    ...state,
    categories: [...state.categories, category.clone()],
    editingCategoryUuid: null,
    error: null,
  }));

  public readonly replaceCategoryAt = this.updater(
    (state, payload: { index: number; category: PipelineStepCategory }) => ({
      ...state,
      categories: state.categories.map((c, i) => (i === payload.index ? payload.category.clone() : c)),
      editingCategoryUuid: null,
      error: null,
    })
  );

  public readonly removeCategory = this.updater((state, uuid: string) => ({
    ...state,
    categories: state.categories.filter((c) => c.uuid !== uuid),
  }));

  public readonly reorderCategories = this.updater((state, uuidOrder: readonly string[]) => {
    const byUuid = new Map(state.categories.map((c) => [c.uuid, c] as const));
    const seen = new Set<string>();
    const reordered: PipelineStepCategory[] = [];
    for (const uuid of uuidOrder) {
      const cat = byUuid.get(uuid);
      if (cat && !seen.has(uuid)) {
        reordered.push(cat);
        seen.add(uuid);
      }
    }
    if (reordered.length !== state.categories.length) return state;
    return { ...state, categories: reordered };
  });

  public readonly setEditingCategoryUuid = this.updater((state, uuid: string | null) => ({
    ...state,
    editingCategoryUuid: uuid,
  }));

  public readonly setMultiple = this.updater((state, multiple: boolean) => ({ ...state, multiple }));
  public readonly setAllowNewOptions = this.updater((state, allowNewOptions: boolean) => ({
    ...state,
    allowNewOptions,
  }));
  public readonly setEnableMosaicView = this.updater((state, enableMosaicView: boolean) => ({
    ...state,
    enableMosaicView,
  }));

  public readonly setCounter = this.updater((state, counter: boolean) => ({ ...state, counter }));

  public readonly enterManualMode = this.updater((state, payload: { value: Record<string, unknown> }) => ({
    ...state,
    manualDraft: { value: payload.value, valid: true },
    error: null,
  }));

  /**
   * Leaves Manual Mode by APPLYING the JSON draft: the view the effects layer
   * projected out of the draft replaces the structured fields, and the draft is
   * dropped — one transition, so no render ever sees half-applied state.
   *
   * `labelNamesError` is part of that one transition rather than set
   * afterwards, because it is *generated by* the very work this update applies:
   * the lookup that named the draft's uuids. Set after the fact it would be a
   * second transition, with a render in between showing the applied draft's
   * uuids and nothing explaining them. Passed in on success too — a lookup that
   * worked clears any outstanding failure, and that clearing has to be as
   * atomic as the reporting.
   */
  public readonly exitManualMode = this.updater((state, applied: ManualModeExit) => ({
    ...state,
    categories: applied.categories.map((category) => category.clone()),
    editingCategoryUuid: null,
    multiple: applied.multiple,
    allowNewOptions: applied.allowNewOptions,
    enableMosaicView: applied.enableMosaicView,
    counter: applied.counter,
    manualDraft: null,
    error: null,
    labelNamesError: applied.labelNamesError,
  }));

  public readonly setManualParams = this.updater((state, payload: ManualDraftState) => {
    if (state.manualDraft === null) return state;
    return { ...state, manualDraft: { value: payload.value, valid: payload.valid } };
  });

  public readonly setBaselineSignature = this.updater((state, signature: string) => ({
    ...state,
    baselineSignature: signature,
  }));

  public readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading }));
  public readonly setError = this.updater((state, error: string | null) => ({ ...state, error }));
}
